import type { EmojiDefinition } from '@atlaskit/adf-schema/emoji';
import { isSSR } from '@atlaskit/editor-common/core-utils';
import type { JSONNode } from '@atlaskit/editor-json-transformer/types';
import type { Node as PMNode } from '@atlaskit/editor-prosemirror/model';
import { defaultEmojiHeight } from '@atlaskit/emoji';
import type {
	EmojiId,
	EmojiProvider,
	EmojiResource,
	OptionalEmojiDescriptionWithVariations,
} from '@atlaskit/emoji';
import { emojiIdToEmoji } from '@atlaskit/emoji/emoji-id-to-emoji';
import { NodeDataProvider } from '@atlaskit/node-data-provider';
import { expValEqualsNoExposure } from '@atlaskit/tmp-editor-statsig/exp-val-equals-no-exposure';

export class EmojiNodeDataProvider extends NodeDataProvider<
	EmojiDefinition,
	OptionalEmojiDescriptionWithVariations
> {
	name = 'emojiNodeDataProvider' as const;
	private readonly emojiResource: EmojiResource;
	private readonly emojiProvider: Promise<EmojiProvider>;

	constructor(resource: EmojiResource) {
		super();

		this.emojiResource = resource;
		this.emojiProvider = resource.getEmojiProvider();
	}

	isNodeSupported(node: JSONNode): node is EmojiDefinition {
		return node.type === 'emoji';
	}

	nodeDataKey(node: EmojiDefinition): string {
		return node.attrs.id || node.attrs.shortName;
	}

	/**
	 * Implementing different re-fetching strategy for emoji.
	 *
	 * Default strategy for NodeDataProvider is to:
	 * 1. If entry is in cache = return from cache
	 * 2. Re-fetch data.
	 * 3. Update cache
	 * 4. Re-render node with fresh data.
	 *
	 * This is similar, but doesn't update the node.
	 * 1. If entry is in cache = return from cache
	 * 2. Re-fetch data.
	 * 3. Update cache
	 */
	getData(
		node: EmojiDefinition | PMNode,
		callback: (
			payload:
				| { data: OptionalEmojiDescriptionWithVariations; error?: undefined }
				| { data?: undefined; error: Error },
		) => void,
	): void {
		const cached = this.getNodeDataFromCache(node);

		if (cached?.data) {
			callback(cached);

			if (!isSSR() && typeof requestAnimationFrame !== 'undefined') {
				requestAnimationFrame(() => void this.getDataAsync(node, () => {}));
			}
		} else {
			void this.getDataAsync(node, callback);
		}
	}

	async fetchNodesData(
		nodes: EmojiDefinition[],
	): Promise<OptionalEmojiDescriptionWithVariations[]> {
		// If we have an `optimisticImageApi`, use it to generate a URL immediately.
		// This is how emojis are server-side rendered in Confluence.
		// Without this, the emoji server response will hit timeout on SSR and the emoji will not be rendered.
		//
		// We are using this optimization only in SSR because on client side we need to know a valid emoji size
		// which is not possible to get from optimisticImageApi.
		//
		// Check platform/packages/editor/renderer/src/react/nodes/emoji.tsx
		// and platform/packages/elements/emoji/src/components/common/ResourcedEmojiComponent.tsx
		const getOptimisticImageUrl = this.emojiResource.emojiProviderConfig.optimisticImageApi?.getUrl;
		if (isSSR() && getOptimisticImageUrl) {
			return nodes.map((node) => {
				if (expValEqualsNoExposure('platform_use_unicode_emojis', 'isEnabled', true)) {
					// Skip SSR for nodes without an Id
					if (!node.attrs.id) {
						return undefined;
					}

					const unicodeEmoji = emojiIdToEmoji(node.attrs.id);
					if (unicodeEmoji) {
						return {
							id: node.attrs.id,
							shortName: node.attrs.shortName,
							fallback: node.attrs.text,
							representation: {
								unicodeEmoji,
							},
							searchable: true,
							type: '',
							category: '',
						};
					}
				}

				const emojiId: EmojiId = {
					id: node.attrs.id,
					shortName: node.attrs.shortName,
					fallback: node.attrs.text,
				};
				const optimisticImageURL = getOptimisticImageUrl(emojiId);

				return {
					id: node.attrs.id,
					shortName: node.attrs.shortName,
					fallback: node.attrs.text,
					representation: {
						height: defaultEmojiHeight,
						width: defaultEmojiHeight,
						imagePath: optimisticImageURL,
					},
					searchable: true,
					// Type and category are unknown at this point.
					// It's not a big deal, because EmojiNodeView doesn't use them.
					type: '',
					category: '',
				};
			});
		}

		// This is slower path because during access to emojiProvider emojiResource
		// makes a request to fetch all emojis.
		const emojiProvider = await this.emojiProvider;

		const fetches = nodes.map(async (node) => {
			const emojiId: EmojiId = {
				id: node.attrs.id,
				shortName: node.attrs.shortName,
				fallback: node.attrs.text,
			};

			// This usually fast because the emojiProvider already has all emojis fetched.
			const result = await emojiProvider.fetchByEmojiId(emojiId, true);

			// For STANDARD emojis, return a UnicodeRepresentation so they are rendered
			// as native text characters rather than images (twemoji removal).
			if (
				node.attrs.id &&
				result &&
				expValEqualsNoExposure('platform_use_unicode_emojis', 'isEnabled', true)
			) {
				const unicodeEmoji = emojiIdToEmoji(node.attrs.id);
				if (unicodeEmoji) {
					return {
						...result,
						representation: {
							unicodeEmoji,
						},
					};
				}
			}

			// If we have optimisticImageApi, we need to path response and set URL from it to match the URL used in SSR.
			if (getOptimisticImageUrl && result) {
				const optimisticImageURL = getOptimisticImageUrl(emojiId);

				if (result.representation && 'imagePath' in result.representation) {
					return {
						...result,
						representation: {
							...result.representation,
							imagePath: optimisticImageURL,
						},
					};
				}
			}

			return result;
		});

		return Promise.all(fetches);
	}
}
